/**
 * Vertical sparkline widget for tooltip display.
 * Layout: Tag name → Sparkline chart → Value with change → Last measured timestamp
 */
import {
  ColorType,
  createChart,
  IChartApi,
  LineData,
  LineSeries,
  Time,
} from 'lightweight-charts'
import { TrendingDown, TrendingUp } from 'lucide-react'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'

const SparklineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: #1a1a1a;
  border-radius: 8px;
`

const TagName = styled.div`
  font-family: 'SF Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
  font-size: 11px;
  font-weight: 500;
  color: #cccccc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ChartWrapper = styled.div`
  width: 100%;
  height: 40px;
`

const ValueRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const CurrentValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
`

const ChangeIndicator = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
`

const LastMeasured = styled.div`
  font-size: 10px;
  color: #666666;
  margin-top: 2px;
`

const UnitLabel = styled.span`
  font-size: 12px;
  color: #888;
  margin-left: 4px;
`

export interface SparklineDataPoint {
  time: number
  value: number
}

interface TooltipSparklineProps {
  piTag: string
  data: SparklineDataPoint[]
  currentValue: number
  previousValue: number
  lastMeasuredAt?: Date
  unit?: string
  color?: string
}

/**
 * Formats a numeric value with appropriate abbreviation for display.
 */
function formatValue(val: number): string {
  if (Math.abs(val) >= 1000000) {
    return `${(val / 1000000).toFixed(1)}M`
  }
  if (Math.abs(val) >= 1000) {
    return `${(val / 1000).toFixed(1)}K`
  }
  if (Math.abs(val) < 0.01 && val !== 0) {
    return val.toExponential(1)
  }
  return val.toFixed(2)
}

/**
 * Formats the last measured timestamp.
 * Shows time if today, otherwise shows date.
 */
function formatLastMeasured(date: Date): string {
  const now = new Date()
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()

  if (isToday) {
    return `Last measured: ${date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}`
  }

  return `Last measured: ${date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`
}

export function TooltipSparkline({
  piTag,
  data,
  currentValue,
  previousValue,
  lastMeasuredAt,
  unit = '',
  color = '#22d3ee',
}: TooltipSparklineProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)

  const change = currentValue - previousValue
  const isPositive = change >= 0
  const changePercent =
    previousValue !== 0 ? ((change / previousValue) * 100).toFixed(1) : '0.0'

  useEffect(() => {
    if (!chartContainerRef.current) return

    const container = chartContainerRef.current
    const width = container.clientWidth || 200

    const chart = createChart(container, {
      width,
      height: 40,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: 'transparent',
        attributionLogo: false,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      crosshair: {
        vertLine: { visible: false },
        horzLine: { visible: false },
      },
      timeScale: {
        visible: false,
        borderVisible: false,
      },
      rightPriceScale: {
        visible: false,
        borderVisible: false,
      },
      leftPriceScale: {
        visible: false,
        borderVisible: false,
      },
      handleScroll: false,
      handleScale: false,
    })

    chartRef.current = chart

    const lineSeries = chart.addSeries(LineSeries, {
      color,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    })

    const chartData: LineData[] = data
      .map((point) => ({
        time: (point.time / 1000) as Time,
        value: point.value,
      }))
      .sort((a, b) => (a.time as number) - (b.time as number))

    if (chartData.length > 0) {
      lineSeries.setData(chartData)
      chart.timeScale().fitContent()
    }

    return () => {
      chart.remove()
      chartRef.current = null
    }
  }, [data, color])

  return (
    <SparklineContainer>
      <TagName title={piTag}>{piTag}</TagName>

      <ChartWrapper ref={chartContainerRef} />

      <ValueRow>
        <CurrentValue>
          {formatValue(currentValue)}
          {unit && <UnitLabel>{unit}</UnitLabel>}
        </CurrentValue>
        <ChangeIndicator $color={color}>
          {isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          {isPositive ? '+' : ''}
          {changePercent}%
        </ChangeIndicator>
      </ValueRow>

      {lastMeasuredAt && (
        <LastMeasured>{formatLastMeasured(lastMeasuredAt)}</LastMeasured>
      )}
    </SparklineContainer>
  )
}
